import { Core } from "@faire/design-tokens";
import { accordionTransitionTiming } from "@faire/retailer-visitor-shared/components/Accordion/Accordion";
import { AnimatedChevronContainer } from "@faire/retailer-visitor-shared/components/Accordion/AnimatedChevronContainer";
import { ExpandedContent } from "@faire/retailer-visitor-shared/components/Accordion/ExpandedContent";
import { FullControlAccordion } from "@faire/retailer-visitor-shared/components/Accordion/FullControlAccordion";
import { Chevron } from "@faire/retailer-visitor-shared/icons/Chevron/Chevron";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import * as React from "react";
import styled, { css } from "styled-components";

interface IProps {
  information: IQuestionAccordionInfo[];
  openIndex: number | undefined;
  onItemClick: (index: number) => void;
  onView?: () => void;
}

export interface IQuestionAccordionInfo {
  question: string;
  answer: React.ReactElement;
  eventKey: string;
}

export const QuestionAccordion: React.FC<IProps> = ({
  information,
  openIndex,
  onItemClick: toggleAccordionItem,
  onView,
}) => {
  const viewRef = useDoActionWhenVisible(onView);
  return (
    <Column
      ref={onView ? viewRef : undefined}
      flex={1}
      style={{ width: "100%" }}
    >
      {information.map(({ question, answer }, index) => (
        <FullControlAccordion
          key={question}
          isOpen={openIndex === index}
          onClick={() => toggleAccordionItem(index)}
        >
          {index === 0 ? <Separator $first={true} /> : null}
          <AccordionItem>
            <Row justify="space-between" align="center">
              <Typography
                as="span"
                variant={{
                  mobileAndAbove: "paragraphSansRegular",
                  tabletAndAbove: "subheadingSansRegular",
                }}
              >
                {question}
              </Typography>
              <AnimatedChevronContainer>
                <Chevron direction="right" height={12} width={12} />
              </AnimatedChevronContainer>
            </Row>
            <ExpandedContent stopPropagation>
              <Spacer size="2x" />
              <Typography
                as="span"
                variant={{
                  mobileAndAbove: "paragraphSansRegular",
                  tabletAndAbove: "subheadingSansRegular",
                }}
              >
                {answer}
              </Typography>
            </ExpandedContent>
          </AccordionItem>
          <Separator />
        </FullControlAccordion>
      ))}
    </Column>
  );
};

const AccordionItem = styled.div`
  padding: ${getSpacing("2x", "4x")};
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("2x", "3x")};
  }
  ${accordionTransitionTiming}
`;

const Separator = styled.hr<{
  $first?: boolean;
}>`
  display: block;
  margin: 0;
  padding: 0;
  border: 0;

  ${responsiveMediaQueries.mobile} {
    ${(props) =>
      props.$first
        ? css`
            display: none;
          `
        : css``}
    height: 1px;
    border-top: 1px solid ${Core.border.muted};
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    height: 1px;
    ${(props) =>
      props.$first
        ? css`
            border: 0.5px solid ${Core.border.subdued};
          `
        : css`
            border-top: 0.5px solid ${Core.border.subdued};
          `}
  }
`;
